import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  Users,
  FileText,
  BookUser,
  Kanban,
  Calendar,
  Settings,
  CheckCircle,
  Target,
  FolderKanban,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import logoImage from "@assets/png_1768006856399.png";
import { useAuth } from "@/lib/auth";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const navigationItems = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  { name: "Buyer Network", href: "/buyers", icon: Building2 },
  { name: "Clients", href: "/clients", icon: Briefcase },
  { name: "Prospects", href: "/prospects", icon: Target },
  { name: "COI", href: "/coi", icon: Users },
  { name: "All Contacts", href: "/contacts", icon: BookUser },
  { name: "Projects", href: "/projects", icon: FolderKanban },
  { name: "Tasks", href: "/tasks", icon: CheckCircle },
];

interface MobileSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const [location] = useLocation();
  const { user } = useAuth();

  const handleNavClick = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[280px] p-0 bg-sidebar text-sidebar-foreground">
        <SheetHeader className="p-4 border-b border-sidebar-border">
          <SheetTitle className="flex items-center gap-3">
            <img 
              src={logoImage} 
              alt="First Turn Capital" 
              className="w-32 h-auto object-contain"
            />
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  href={item.href}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 group touch-manipulation min-h-[48px]",
                    isActive
                      ? "sidebar-active bg-sidebar-accent text-white font-semibold"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-white active:bg-sidebar-accent/70"
                  )}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className={cn("w-5 h-5 flex-shrink-0", isActive ? "text-primary" : "text-sidebar-foreground/70 group-hover:text-white")} />
                  <span className="text-sm">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-sidebar-border p-3 bg-sidebar flex-shrink-0">
            <Link
              href="/settings"
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 group touch-manipulation min-h-[48px]",
                location === "/settings"
                  ? "sidebar-active bg-sidebar-accent text-white font-semibold"
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-white active:bg-sidebar-accent/70"
              )}
              data-testid="mobile-nav-settings"
            >
              <Settings className={cn("w-5 h-5 flex-shrink-0", location === "/settings" ? "text-primary" : "text-sidebar-foreground/70 group-hover:text-white")} />
              <span className="text-sm font-medium">Settings</span>
            </Link>

            <div className="mt-3 pt-3 border-t border-sidebar-border flex items-center gap-3 px-3">
              <Avatar className="w-10 h-10 ring-2 ring-primary/30">
                <AvatarImage src={user?.profileImageUrl || ""} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{user?.initials || "?"}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.name || "User"}</p>
                <p className="text-xs text-sidebar-foreground/70 truncate">{user?.email || ""}</p>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
